import React from "react";
import { Route, Navigate } from "react-router-dom";
import { RouteConfig, RouteSection, NavigationItem } from "./types";
import ProtectedPageRoute from "../../components/admin/ProtectedPageRoute";

/**
 * Generate React Router routes from route configuration
 */
export function generateRoutes(
  section: RouteSection,
  userPermissions?: string[],
): React.ReactElement[] {
  const routes: React.ReactElement[] = [];

  // Generate routes for each config
  section.routes.forEach((routeConfig) => {
    // Wrap admin routes with ProtectedPageRoute to check if page is disabled
    let element =
      section.basePath === "/admin" &&
      routeConfig.permission &&
      userPermissions ? (
        <ProtectedPageRoute
          pageIdentifier={routeConfig.permission}
          userPermissions={userPermissions}
        >
          <routeConfig.element />
        </ProtectedPageRoute>
      ) : (
        <routeConfig.element />
      );

    routes.push(
      <Route
        key={routeConfig.path}
        path={routeConfig.path}
        element={element}
      />,
    );

    // Handle child routes if any
    if (routeConfig.children) {
      routeConfig.children.forEach((child) => {
        const childPath = `${routeConfig.path}/${child.path}`;
        let childElement =
          section.basePath === "/admin" &&
          child.permission &&
          userPermissions ? (
            <ProtectedPageRoute
              pageIdentifier={child.permission}
              userPermissions={userPermissions}
            >
              <child.element />
            </ProtectedPageRoute>
          ) : (
            <child.element />
          );

        routes.push(
          <Route
            key={childPath}
            path={childPath}
            element={childElement}
          />,
        );
      });
    }
  });

  // Add default redirect if specified
  if (section.defaultPath) {
    routes.push(
      <Route
        key="default-redirect"
        index
        element={<Navigate replace to={section.defaultPath} />}
      />,
    );
  }

  return routes;
}

/**
 * Generate navigation items from route configuration
 */
export function generateNavigation(
  routes: RouteConfig[],
  basePath: string = "",
): NavigationItem[] {
  const navItems: NavigationItem[] = [];

  routes.forEach((route) => {
    if (route.showInNav) {
      const fullPath = basePath ? `${basePath}/${route.path}` : route.path;

      const navItem: NavigationItem = {
        to: fullPath,
        permission: route.permission,
        icon: route.icon,
        textKey: route.titleKey,
        defaultText: route.title,
        notificationCountKey: route.notificationKey,
        group: route.meta?.group,
      };

      // Handle child routes
      if (route.children) {
        const childNavItems = generateNavigation(route.children, fullPath);
        if (childNavItems.length > 0) {
          navItem.children = childNavItems;
        }
      }

      navItems.push(navItem);
    }
  });

  return navItems;
}

/**
 * Get page title for current path
 */
export function getPageTitle(
  pathname: string,
  routes: RouteConfig[],
  basePath: string = "",
): string | null {
  // Remove leading slash and base path
  const cleanPath = pathname
    .replace(/^\//, "")
    .replace(new RegExp(`^${basePath.replace(/^\//, "")}/`), "");

  for (const route of routes) {
    if (cleanPath === route.path || cleanPath === "") {
      return route.title;
    }

    // Check if this route matches the beginning of the path
    if (cleanPath.startsWith(route.path + "/") && route.children) {
      const childPath = cleanPath.replace(route.path + "/", "");
      const childTitle = getPageTitle("/" + childPath, route.children);
      if (childTitle) {
        return childTitle;
      }
    }
  }

  return null;
}

/**
 * Get page title key for translations
 */
export function getPageTitleKey(
  pathname: string,
  routes: RouteConfig[],
  basePath: string = "",
): string | null {
  const cleanPath = pathname
    .replace(/^\//, "")
    .replace(new RegExp(`^${basePath.replace(/^\//, "")}/`), "");

  for (const route of routes) {
    if (cleanPath === route.path || cleanPath === "") {
      return route.titleKey;
    }

    if (cleanPath.startsWith(route.path + "/") && route.children) {
      const childPath = cleanPath.replace(route.path + "/", "");
      const childTitleKey = getPageTitleKey("/" + childPath, route.children);
      if (childTitleKey) {
        return childTitleKey;
      }
    }
  }

  return null;
}

/**
 * Check if user has permission for a route
 */
export function checkRoutePermission(
  route: RouteConfig,
  userPermissions: string[],
): boolean {
  if (!route.permission) {
    return true; // No permission required
  }

  return userPermissions.includes(route.permission);
}

/**
 * Filter routes based on user permissions
 */
export function filterRoutesByPermission(
  routes: RouteConfig[],
  userPermissions: string[],
): RouteConfig[] {
  return routes.filter((route) => {
    if (!checkRoutePermission(route, userPermissions)) {
      return false;
    }

    // Filter children recursively
    if (route.children) {
      route.children = filterRoutesByPermission(
        route.children,
        userPermissions,
      );
    }

    return true;
  });
}

/**
 * Generate breadcrumb items for a path
 */
export function generateBreadcrumbs(
  pathname: string,
  routes: RouteConfig[],
  basePath: string = "",
): Array<{ path: string; title: string; titleKey: string }> {
  const breadcrumbs: Array<{ path: string; title: string; titleKey: string }> =
    [];
  const cleanPath = pathname
    .replace(/^\//, "")
    .replace(new RegExp(`^${basePath.replace(/^\//, "")}/`), "");
  const pathSegments = cleanPath.split("/").filter(Boolean);

  let currentPath = basePath;
  let currentRoutes = routes;

  for (const segment of pathSegments) {
    const route = currentRoutes.find((r) => r.path === segment);

    if (route && route.meta?.breadcrumb !== false) {
      currentPath = currentPath ? `${currentPath}/${route.path}` : route.path;
      breadcrumbs.push({
        path: currentPath,
        title: route.title,
        titleKey: route.titleKey,
      });

      if (route.children) {
        currentRoutes = route.children;
      }
    }
  }

  return breadcrumbs;
}

/**
 * Find route configuration by path
 */
export function findRouteByPath(
  pathname: string,
  routes: RouteConfig[],
  basePath: string = "",
): RouteConfig | null {
  // Special handling for guest routes with token parameter
  let cleanPath = pathname;

  if (basePath === "/g/:token" && pathname.startsWith("/g/")) {
    // Extract the path after /g/{token}/
    const parts = pathname.split("/");
    if (parts.length >= 3) {
      // parts[0] = '', parts[1] = 'g', parts[2] = token, parts[3]+ = actual path
      cleanPath = parts.slice(3).join("/");
    }
  } else {
    // Original logic for non-parameterized paths
    cleanPath = pathname
      .replace(/^\//, "")
      .replace(new RegExp(`^${basePath.replace(/^\//, "")}/`), "");
  }

  for (const route of routes) {
    if (cleanPath === route.path || (cleanPath === "" && route.path === "")) {
      return route;
    }

    if (cleanPath.startsWith(route.path + "/") && route.children) {
      const childPath = cleanPath.replace(route.path + "/", "");
      const childRoute = findRouteByPath("/" + childPath, route.children);
      if (childRoute) {
        return childRoute;
      }
    }
  }

  return null;
}

/**
 * Group navigation items by their group metadata
 */
export function groupNavigationItems(
  items: NavigationItem[],
): Record<string, NavigationItem[]> {
  const grouped: Record<string, NavigationItem[]> = {};

  items.forEach((item) => {
    const group = item.group || "other";
    if (!grouped[group]) {
      grouped[group] = [];
    }
    grouped[group].push(item);
  });

  return grouped;
}
